import React, { useCallback, useMemo, useState } from 'react';
import { Activity, ArrowLeft, Heart, MailCheck, Share2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import logoSrc from '../../assets/mywellfie-header-logo.png';
import reportLogoSrc from '../../assets/mywellfie-full-logo.png';
import type { AdhocPackInfo } from '../../api/adhocApi';
import './landing.css';

export interface AdhocPackHubStageProps {
  pack: AdhocPackInfo;
  isOwner: boolean;
  paying: boolean;
  onStartScan: () => void;
  onCopyLink: () => void;
  onBack: () => void;
}

const GUEST_BENEFIT_CARDS: ReadonlyArray<{
  title: string;
  body: string;
  Icon: LucideIcon;
}> = [
  {
    title: '60-Second Scan',
    body: 'Hold still for a face scan on your phone. No cuffs, clips, or lab visit.',
    Icon: Activity,
  },
  {
    title: 'Vital Markers',
    body: 'Pulse, blood pressure, stress and key wellness markers.',
    Icon: Heart,
  },
  {
    title: 'Instant Report',
    body: 'Full health report emailed to you.',
    Icon: MailCheck,
  },
];

const GUEST_STEPS = [
  { title: 'Tap Start my scan', body: 'Begin your session — no payment needed.' },
  { title: 'Enter your details', body: 'Add your name and email for your report.' },
  { title: 'Hold still for the camera', body: 'Results arrive in your inbox within seconds.' },
] as const;

const AdhocPackHubStage: React.FC<AdhocPackHubStageProps> = ({
  pack,
  isOwner,
  paying,
  onStartScan,
  onCopyLink,
  onBack,
}) => {
  const [copied, setCopied] = useState(false);
  const canNativeShare = useMemo(
    () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
    [],
  );

  const copyShareUrl = useCallback(async () => {
    if (!pack.share_url) return false;
    await navigator.clipboard.writeText(pack.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    return true;
  }, [pack.share_url]);

  const handleCopy = useCallback(async () => {
    if (!pack.share_url) return;
    onCopyLink();
    try {
      await copyShareUrl();
    } catch {
      /* fallback: user can select from input */
    }
  }, [copyShareUrl, onCopyLink, pack.share_url]);

  const handleShare = useCallback(async () => {
    if (!pack.share_url) return;
    onCopyLink();
    try {
      await navigator.share({
        title: 'MyWellfie scan',
        text: 'Use this link to take your MyWellfie health scan.',
        url: pack.share_url,
      });
    } catch (e: unknown) {
      if ((e as Error)?.name === 'AbortError') return;
      try {
        await copyShareUrl();
      } catch {
        /* clipboard blocked */
      }
    }
  }, [copyShareUrl, onCopyLink, pack.share_url]);

  const depleted = pack.scans_remaining <= 0;

  const startScanButton = (
    <button
      type="button"
      className="btn btn-offer pack-hub-start-btn"
      disabled={paying}
      onClick={onStartScan}
    >
      {paying ? (
        'Starting…'
      ) : (
        <>
          Start my scan
          <span className="btn-arrow" aria-hidden>→</span>
        </>
      )}
    </button>
  );

  return (
    <div className="adhoc-landing pack-hub-page">
      <nav>
        <div className="wrap">
          <div className="nav-row-split">
            <div className="nav-logo">
              <img src={logoSrc} alt="MyWellfie" />
            </div>
            <button
              type="button"
              className="btn btn-ghost family-pack-nav-back"
              onClick={onBack}
            >
              <ArrowLeft size={16} aria-hidden />
              Back
            </button>
          </div>
        </div>
      </nav>

      <div className="pack-hub-page-main">
        <div className="wrap pack-hub-card">
          {isOwner ? (
            <>
              <span className="eyebrow">Multiple scans</span>
              <h1 className="pack-hub-title">{pack.label}</h1>
              <p className="pack-hub-lede">{pack.subtitle}</p>
            </>
          ) : (
            <>
              <span className="eyebrow">Shared scan</span>
              <h1 className="pack-hub-title">Your free health scan</h1>
              <p className="pack-hub-lede">
                You&apos;ve been invited to a MyWellfie scan. It&apos;s already paid for — no checkout needed.
              </p>
            </>
          )}

          {!depleted && isOwner && (
            <div className="pack-hub-credits">
              <div className="pack-hub-credits-num">{pack.scans_remaining}</div>
              <div className="pack-hub-credits-label">
                scan{pack.scans_remaining === 1 ? '' : 's'} remaining
              </div>
            </div>
          )}

          {!depleted && isOwner && (
            <>
              {startScanButton}
              {pack.share_url && (
                <>
                  <div className="pack-hub-share-heading">Share with family</div>
                  <div className="pack-hub-share-row">
                    <input
                      readOnly
                      value={pack.share_url}
                      className="pack-hub-share-input"
                      onFocus={(e) => e.target.select()}
                    />
                    {canNativeShare && (
                      <button type="button" className="btn btn-copy" onClick={handleShare}>
                        <Share2 size={14} aria-hidden />
                        Share
                      </button>
                    )}
                    <button type="button" className="btn btn-copy" onClick={handleCopy}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="pack-hub-fine">
                    Anyone with this link can take a scan while credits remain. Each person enters their own name and email.
                  </p>
                </>
              )}
            </>
          )}

          {!depleted && !isOwner && (
            <>
              <div className="pack-hub-guest-cta-wrap">
                {startScanButton}
              </div>

              <section className="pack-hub-guest-section pack-hub-guest-benefits">
                <h2 className="pack-hub-guest-heading">What you&apos;ll get</h2>
                <div className="pack-hub-guest-benefit-cards">
                  {GUEST_BENEFIT_CARDS.map(({ title, body, Icon }) => (
                    <article key={title} className="pack-hub-guest-benefit-card">
                      <div className="pack-hub-guest-benefit-icon" aria-hidden>
                        <Icon size={20} strokeWidth={2.2} />
                      </div>
                      <div>
                        <h3 className="pack-hub-guest-benefit-title">{title}</h3>
                        <p className="pack-hub-guest-benefit-body">{body}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <section className="pack-hub-guest-section">
                <h2 className="pack-hub-guest-heading">How it works</h2>
                <ol className="pack-hub-guest-steps">
                  {GUEST_STEPS.map((step, index) => (
                    <li key={step.title}>
                      <span className="pack-hub-guest-step-num">{index + 1}</span>
                      <div>
                        <h3>{step.title}</h3>
                        <p>{step.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}

          {depleted && (
            <p className="pack-hub-lede pack-hub-depleted">
              {isOwner
                ? 'All scans from this pack have been used.'
                : 'All shared scans have been used. Ask the person who sent you this link for a new one.'}
            </p>
          )}
        </div>
      </div>

      <footer>
        <div className="wrap">
          <div className="brand">
            <img src={reportLogoSrc} alt="MyWellfie" />
          </div>
          <span className="fine">
            MyWellfie reports are for informational purposes only and don&apos;t replace
            professional medical advice.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default AdhocPackHubStage;
